import { unwrapPrivateAPI } from '@mui/x-data-grid/internals';
import {
  type GridApiPremium,
  type GridPrivateApiPremium,
  type GridRowId,
  gridRowTreeSelector,
  type MuiEvent,
} from '@mui/x-data-grid-premium';
import { isNil } from 'lodash';
import { useEffect } from 'react';
import type { GridApiRef } from 'src/modules/grids/DataGrid';
import { useEditableGridRows } from 'src/modules/grids/editable/useEditableGridRows';
import { createKeybindingsHandler, type KeybindingsMap } from 'tinykeys';
import { type NavDirection, useGetAdjacentCell, useGetAdjacentRow, useGetFirstEditableCell } from './adjacency';
import useGoToCell from './useGoToCell';

export const POPOVER_EDIT_CELL_SELECTOR = '.PopoverEditCell';
const GRID_CELL_SELECTOR = '.MuiDataGrid-cell';
const GRID_ROW_SELECTOR = '.MuiDataGrid-row';

// Grid-instance-qualified (Task 0.4). Resolve via document.getElementById, NEVER a '#…'
// querySelector — instanceGridId contains CSS-significant colons from React useId.
export const cellContentsId = (instanceGridId: string, rowId: GridRowId, field: string) =>
  `cell-contents-${instanceGridId}-${rowId}-${field}`;

/** Installs the document-level grid keymap (portaled popover editors escape the grid DOM,
 *  so this MUST stay a document listener — see PR #4203). */
export function useGridKeyboardNav<R extends object>(
  apiRef: GridApiRef,
  opts: { canAdd?: boolean; createFreshRow: (source?: Partial<R>) => R; instanceGridId: string }
): void {
  const { canAdd, createFreshRow, instanceGridId } = opts;
  const addDraft = useEditableGridRows((store) => store.addDraft);
  const getLocalRow = useEditableGridRows((store) => store.getLocalRow);
  const goToCell = useGoToCell(apiRef);
  const getAdjacentCell = useGetAdjacentCell(apiRef);
  const getAdjacentRow = useGetAdjacentRow(apiRef);
  const getFirstEditableCell = useGetFirstEditableCell(apiRef);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;

    const resolveActiveCell = (allowViewMode = false) => {
      const popover = document.activeElement?.closest(POPOVER_EDIT_CELL_SELECTOR);
      const cellElement = document.activeElement?.closest(GRID_CELL_SELECTOR);
      const rowElement = cellElement?.closest(GRID_ROW_SELECTOR);

      if (!popover && !cellElement) {
        return null;
      }

      // Portaled editors sit outside gridRoot, so require their exact instance
      // stamp as a defense-in-depth ownership check before handling navigation.
      if (popover && popover.getAttribute('data-popover-grid') !== instanceGridId) {
        return null;
      }

      const gridRoot = api.rootElementRef?.current;
      if (cellElement && !gridRoot?.contains(document.activeElement)) {
        return null;
      }

      // Derive the active cell from the focused edit surface (the portaled popover, or
      // the cell) — the browser focus is the source of truth. Tracking it in a ref
      // desynced for boolean cells whose checkbox is portaled out of the grid, which
      // made us bail and let MUI's native row-edit Tab land on the next column-editable
      // cell — an input-less BlankCell on non-editable-for-this-row columns, dropping
      // focus to <body> and escaping the grid.
      const activeField = popover?.getAttribute('data-popover-field') ?? cellElement?.getAttribute('data-field');
      const activeRowAttr = popover?.getAttribute('data-popover-row') ?? rowElement?.getAttribute('data-id');
      const activeRowId = api.getAllRowIds().find((id) => String(id) === activeRowAttr);

      if (!activeField || isNil(activeRowId) || (!allowViewMode && api.getRowMode(activeRowId) !== 'edit')) {
        return null;
      }

      if (popover && api.getColumnIndex(activeField) === -1) {
        return null;
      }

      return { activeField, activeRowId };
    };

    /**
     * Drains the editor's pending debounced value before the cell is left.
     *
     *   type "Zed" ─▶ setEditCellValue, DEBOUNCED 200ms  GridEditInputCell.js:44
     *        │           (the row still reads "Alpha")
     *        └─ Tab ─▶ this keymap ─▶ FLUSH ◄── here ─▶ blur ─▶ goToCell
     *
     * Intra-row Tab/Shift+Tab keep the row in edit mode and this keymap
     * `stopPropagation()`s the key, so MUI's own stop paths never run the drain.
     *
     * Called row-wide (no `field`), exactly as MUI's stop paths call it: any cell
     * of the row can hold a pending debounce, and the drained callback re-checks
     * `getCellMode === 'edit'` per field before applying (useGridEditing.js:96).
     *
     * MUI v9 still exposes this drain only on the private API. Keep the unwrap
     * isolated here and protect its observable behavior with the keymap contract.
     */
    const flushPendingEditorValues = (rowId: GridRowId): void => {
      // A test double carries no MUI private stamp, so the unwrap yields nothing.
      const privateApi: GridPrivateApiPremium | undefined = unwrapPrivateAPI<GridPrivateApiPremium, GridApiPremium>(
        api
      );
      privateApi?.runPendingEditCellValueMutation(rowId);
    };

    const handleTab = (e: KeyboardEvent) => {
      // Enter on the last row commits in place and leaves its cell in VIEW mode (R-B), and the
      // grid's onKeyDown swallows every Tab, so a view-mode cell has to be answered here too or
      // the keyboard is stuck on it (QA1013 #1/#4: a one-line order picked by Enter).
      const editingCell = resolveActiveCell();
      const activeCell = editingCell ?? resolveActiveCell(true);
      if (!activeCell) {
        return;
      }
      const { activeField, activeRowId } = activeCell;
      const fromViewMode = !editingCell;
      const direction: NavDirection = e.shiftKey ? 'backward' : 'forward';

      // We are leaving this cell no matter which branch wins below — navigate,
      // wrap, mint a row, or decline back to MUI — so drain before any of them.
      flushPendingEditorValues(activeRowId);

      let gotoRowId = activeRowId;
      let gotoColumnIndex: number = -1;

      // Move within current row to adjacent editable cell (getAdjacentCell honours the
      // per-row isCellEditable, so non-editable/input-less columns are skipped here).
      const nextField = getAdjacentCell(activeRowId, activeField, direction);
      gotoColumnIndex = isNil(nextField) ? -1 : api.getColumnIndex(nextField);
      // If at edge of row, move to adjacent row and goto first editable cell
      if (gotoColumnIndex === -1) {
        const nextRow = getAdjacentRow(activeRowId, direction);

        if (!isNil(nextRow)) {
          gotoRowId = nextRow;
          gotoColumnIndex = getFirstEditableCell(gotoRowId, direction) ?? -1;
        }
      }

      // There wasn't a next row, so we create a new one and nav to it. Only out of an open editor:
      // Tab on a cell the user already committed is not a request for a new line.
      if (gotoColumnIndex === -1 && canAdd && !e.shiftKey && !fromViewMode) {
        const parentId = gridRowTreeSelector(apiRef)[activeRowId].parent;
        const parent = isNil(parentId) ? undefined : getLocalRow(parentId);
        const newRow = addDraft(createFreshRow(parent));

        api.updateRows([newRow]);
        api.stopRowEditMode({ id: activeRowId });

        gotoColumnIndex = getFirstEditableCell(newRow.rowId, 'forward') ?? -1;
        gotoRowId = newRow.rowId;
      }

      if (gotoColumnIndex === -1 || isNil(gotoRowId)) {
        return;
      }

      const contents = document.getElementById(cellContentsId(instanceGridId, activeRowId, activeField));
      const columnDef = api.getColumn(activeField);
      if (!columnDef) return;

      const focusableElement =
        'focusableElement' in columnDef && typeof columnDef.focusableElement === 'string'
          ? columnDef.focusableElement
          : 'input';
      const input = contents?.querySelector<HTMLElement>(focusableElement);

      // we stop propagation, so we need to handle bluring the input ourselves
      if (input) {
        input.blur();
      }

      e.preventDefault();
      e.stopPropagation();

      goToCell(gotoColumnIndex, gotoRowId);
    };

    const handleShiftEnter = (e: KeyboardEvent) => {
      // The initial top-row press commits into view mode. Its physical key repeat
      // still belongs to this grid and must not fall through to native Enter-down.
      const activeCell = resolveActiveCell(e.repeat);
      if (!activeCell) {
        return;
      }
      const { activeField, activeRowId } = activeCell;
      const previousRowId = getAdjacentRow(activeRowId, 'backward');

      // Shift+Enter always belongs to this keymap once it reaches an owned edit
      // cell. At the first row, commit in place without letting native Enter-down
      // move focus to the next row.
      e.preventDefault();
      e.stopPropagation();
      const muiEvent: MuiEvent<KeyboardEvent> = e;
      muiEvent.defaultMuiPrevented = true;

      if (e.repeat) {
        return;
      }

      api.stopRowEditMode({ id: activeRowId });

      if (!isNil(previousRowId)) {
        goToCell(api.getColumnIndex(activeField), previousRowId);
      }
    };

    const keybindings = {
      '[Shift]+[Control]+[Alt]+[Meta]+Tab': handleTab,
      'Shift+Enter': handleShiftEnter,
    } satisfies KeybindingsMap;
    const handleKeydown = createKeybindingsHandler(keybindings, {
      // Preserve every former Tab event, including repeats and composition. The new
      // exact Shift+Enter chord yields to IME events, as MUI's native handler does.
      ignore: (event) =>
        event.key !== 'Tab' &&
        (event.key !== 'Enter' ||
          !event.shiftKey ||
          event.ctrlKey ||
          event.altKey ||
          event.metaKey ||
          event.isComposing ||
          event.which === 229),
    });

    // Capture phase is load-bearing: MUI's own row-edit Tab handler runs in the
    // target/bubble phase and moves focus to the next COLUMN-editable cell first — on a
    // non-editable-for-this-row column that's an input-less BlankCell, so focus drops to
    // <body>. A bubble-phase listener reads document.activeElement AFTER that move (===
    // <body>) and bails at the guard above. Running in capture lets us read the real
    // source cell, skip the input-less column via getAdjacentCell, and stopPropagation to
    // preempt MUI before it ever escapes the grid.
    document.addEventListener('keydown', handleKeydown, true);

    return () => {
      document.removeEventListener('keydown', handleKeydown, true);
    };
  }, [
    apiRef,
    getAdjacentCell,
    getAdjacentRow,
    getFirstEditableCell,
    goToCell,
    canAdd,
    getLocalRow,
    addDraft,
    createFreshRow,
    instanceGridId,
  ]);
}
